"use client";

import { queryOptions, useQuery } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { Info, TriangleAlert, Download } from "lucide-react";

import { Link } from "@/i18n/navigation";
import { api } from "@/shared/lib/api";
import { SHOW_TAURI_FEATURES } from "@/shared/config";
import { QUERY_VOLATILITY } from "@/shared/config/api-constants";

type Coverage = {
  totalMatches: number;
  matchesWithData: number;
  coveragePercent: number;
  /** Ожидаемых строк «игрок в матче» — по составам сторон. */
  expectedPlayerEntries?: number;
  /** Из них реально со статистикой. */
  playerEntriesWithData?: number;
  playerCoveragePercent?: number;
  /** Игроков в реплеях, не привязанных ни к какому аккаунту. */
  unmatchedReplayEntries?: number;
};

export const tournamentStatsCoverageQuery = (tournamentId: string) =>
  queryOptions({
    queryKey: ["tournaments", tournamentId, "stats-coverage"],
    queryFn: () => fetchCoverage(tournamentId),
    ...QUERY_VOLATILITY.STANDARD,
    // Покрытие — вспомогательная плашка: если эндпоинт лёг, страница итогов
    // должна остаться целой, а не показывать ошибку вместо статистики.
    retry: false,
  });

async function fetchCoverage(tournamentId: string) {
  const res = await api.get<Coverage>(
    `/tournaments/${encodeURIComponent(tournamentId)}/stats-coverage`,
  );
  return res.data;
}

/**
 * Честная плашка о покрытии статистики: по скольким матчам вообще есть данные.
 *
 * - 0 сыгранных матчей → ничего не показываем
 * - 0 матчей с данными → «подробной статистики нет»
 * - частично → предупреждение «данные по X из Y»
 * - полностью → ничего (не засоряем)
 *
 * Плашка НЕ спрятана за SHOW_TAURI_FEATURES: сам факт неполноты данных читателю
 * нужен всегда, иначе полупустая страница итогов выглядит сломанной. От флага
 * зависит только объяснение «почему» — звать в десктоп-приложение имеет смысл
 * лишь когда оно раздаётся.
 */
export function TournamentStatsCoverageNote({
  tournamentId,
}: {
  tournamentId: string;
}) {
  const tUi = useTranslations("Ui");
  const { data, isLoading } = useQuery({
    ...tournamentStatsCoverageQuery(tournamentId),
    enabled: Boolean(tournamentId),
  });

  if (isLoading || !data) return null;
  const {
    totalMatches,
    matchesWithData,
    expectedPlayerEntries = 0,
    playerEntriesWithData = 0,
  } = data;

  if (totalMatches === 0) return null;

  // Разрез по игрокам важнее матчевого: на августовском турнире по матчам было
  // честные 100%, а у пятерых участников статистики не было вовсе — в играх,
  // где данные пришли из десктоп-клиента, доезжало 8 игроков из 10.
  const playersMissing = Math.max(0, expectedPlayerEntries - playerEntriesWithData);

  // Полное покрытие в обоих разрезах — без шума.
  if (matchesWithData >= totalMatches && playersMissing === 0) return null;

  // Матчи покрыты, но часть игроков без данных — раньше здесь молчали.
  if (matchesWithData >= totalMatches) {
    return (
      <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
        <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
        <div className="min-w-0">
          <p className="text-foreground">
            Статистика неполная — она есть у{" "}
            <span className="font-semibold tabular-nums">
              {playerEntriesWithData} из {expectedPlayerEntries}
            </span>{" "}
            участников матчей.
          </p>
          <p className="text-muted-foreground">
            {tUi("tournamentStatsCoverageNote.poOstalnymDannyeNe")}
          </p>
        </div>
      </div>
    );
  }

  // Совсем нет данных.
  if (matchesWithData === 0) {
    // Без десктоп-фич звать «скачайте приложение» некуда, но промолчать нельзя:
    // читатель должен понимать, что пустая статистика — это отсутствие данных,
    // а не сломанная страница.
    if (!SHOW_TAURI_FEATURES) {
      return (
        <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3 text-sm">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
          <p className="text-muted-foreground">
            {tUi("tournamentStatsCoverageNote.podrobnoyStatistikiPoMatcham")}
          </p>
        </div>
      );
    }
    return (
      <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 p-3 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0">
          <p className="text-foreground">
            {tUi("tournamentStatsCoverageNote.podrobnayaStatistikaPoEtomu")}
          </p>
          <p className="text-muted-foreground">
            Пики, KDA и другие данные собираются автоматически только когда
            игроки используют десктоп-приложение GoodLuck во время матчей.{" "}
            <Link
              href="/download"
              className="inline-flex items-center gap-1 text-primary hover:underline"
            >
              <Download className="h-3.5 w-3.5" />
              {tUi("tournamentStatsCoverageNote.skachatPrilozhenie")}
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Частичное покрытие.
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-500/30 bg-amber-500/5 p-3 text-sm">
      <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
      <div className="min-w-0">
        <p className="text-foreground">
          Статистика неполная — данные есть по{" "}
          <span className="font-semibold tabular-nums">
            {matchesWithData} из {totalMatches}
          </span>{" "}
          матчей.
        </p>
        <p className="text-muted-foreground">
          {SHOW_TAURI_FEATURES
            ? tUi("litTournamentStatsCoverageNote.sobirayutsyaTolkoTamGde")
            : tUi("litTournamentStatsCoverageNote.poOstalnymMatchamDannye")}
        </p>
      </div>
    </div>
  );
}
